import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Loader2, Search, X } from "lucide-react";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { adminGetOrders } from "@/lib/adminApi";
import { ADMIN_ORDERS } from "@/constants/testIds/admin";

const ORDER_STATUS = {
  draft: { label: "Draf", cls: "gray" },
  pending_payment: { label: "Menunggu Bayaran", cls: "amber" },
  payment_confirmed: { label: "Bayaran Disahkan", cls: "teal" },
  processing: { label: "Sedang Diproses", cls: "blue" },
  completed: { label: "Selesai", cls: "green" },
  cancelled: { label: "Dibatalkan", cls: "red" },
};
const PAYMENT_STATUS = {
  unpaid: { label: "Belum Bayar", cls: "amber" },
  pending: { label: "Menunggu", cls: "amber" },
  paid: { label: "Telah Bayar", cls: "green" },
  failed: { label: "Gagal", cls: "red" },
  expired: { label: "Tamat Tempoh", cls: "gray" },
  refunded: { label: "Dikembalikan", cls: "gray" },
};
const ARTWORK_STATUS = {
  not_generated: { label: "Belum Dijana", cls: "gray" },
  pending: { label: "Menunggu", cls: "amber" },
  generated: { label: "Telah Dijana", cls: "green" },
  approved: { label: "Diluluskan", cls: "green" },
  failed: { label: "Gagal", cls: "red" },
};

const BADGE_CLS = {
  amber: "bg-amber-400/10 text-amber-300 ring-amber-400/30",
  teal: "bg-teal-400/10 text-teal-300 ring-teal-400/30",
  blue: "bg-sky-400/10 text-sky-300 ring-sky-400/30",
  green: "bg-emerald-400/10 text-emerald-300 ring-emerald-400/30",
  red: "bg-rose-400/10 text-rose-300 ring-rose-400/30",
  gray: "bg-white/5 text-[#a9c4b8] ring-white/15",
};

const Badge = ({ map, value }) => {
  const item = map[value] || { label: value || "—", cls: "gray" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${BADGE_CLS[item.cls]}`}
    >
      {item.label}
    </span>
  );
};

const money = (v) =>
  `RM${Number(v || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const selectCls =
  "w-full rounded-xl bg-[#0b1f18] text-white ring-1 ring-white/15 focus:ring-2 focus:ring-[#c79a3b] outline-none px-3 py-2.5 text-sm transition";

const FilterSelect = ({ testId, label, value, onChange, options }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#a9c4b8]">
      {label}
    </span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 ${selectCls}`}
    >
      <option value="">Semua</option>
      {options.map(([val, lbl]) => (
        <option key={val} value={val}>
          {lbl}
        </option>
      ))}
    </select>
  </label>
);

const toOptions = (map) => Object.entries(map).map(([k, v]) => [k, v.label]);

export default function AdminOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const search = searchParams.get("search") || "";
  const orderStatus = searchParams.get("order_status") || "";
  const paymentStatus = searchParams.get("payment_status") || "";
  const artworkStatus = searchParams.get("artwork_status") || "";
  const product = searchParams.get("product_code") || "";

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const skipDebounce = useRef(false);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const params = { page, page_size: 20 };
    if (search) params.search = search;
    if (orderStatus) params.order_status = orderStatus;
    if (paymentStatus) params.payment_status = paymentStatus;
    if (artworkStatus) params.artwork_status = artworkStatus;
    if (product) params.product_code = product;
    adminGetOrders(params)
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 401) {
          navigate("/admin/login", { replace: true });
          return;
        }
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, search, orderStatus, paymentStatus, artworkStatus, product, navigate]);

  const update = (patch, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v);
      else next.delete(k);
    });
    if (resetPage) next.set("page", "1");
    setSearchParams(next);
  };

  // Debounced search -> URL (resets to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      if (skipDebounce.current) {
        skipDebounce.current = false;
        return;
      }
      if (searchInput !== search) update({ search: searchInput });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const hasFilters =
    search || orderStatus || paymentStatus || artworkStatus || product;

  const clearFilters = () => {
    skipDebounce.current = true;
    setSearchInput("");
    setSearchParams(new URLSearchParams());
  };

  const totalPages = data?.total_pages || 1;
  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#0b1f18] text-white" data-testid={ADMIN_ORDERS.page}>
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-extrabold">Tempahan</h1>
            <p className="mt-1 text-sm text-[#a9c4b8]" data-testid={ADMIN_ORDERS.total}>
              {data ? `${data.total} tempahan` : "\u00a0"}
            </p>
          </div>
        </div>

        {/* Search + filters */}
        <div className="mt-6 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4 sm:p-5 space-y-4">
          <div className="relative">
            <Search
              strokeWidth={1.75}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5c7268]"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cari no. tempahan, perniagaan, nama, telefon, emel..."
              data-testid={ADMIN_ORDERS.search}
              className="w-full rounded-xl bg-[#0b1f18] text-white placeholder-[#5c7268] ring-1 ring-white/15 focus:ring-2 focus:ring-[#c79a3b] outline-none pl-10 pr-4 py-3 text-sm transition"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterSelect
              testId={ADMIN_ORDERS.filterOrderStatus}
              label="Status Tempahan"
              value={orderStatus}
              onChange={(v) => update({ order_status: v })}
              options={toOptions(ORDER_STATUS).filter(([v]) => v !== "draft")}
            />
            <FilterSelect
              testId={ADMIN_ORDERS.filterPaymentStatus}
              label="Bayaran"
              value={paymentStatus}
              onChange={(v) => update({ payment_status: v })}
              options={toOptions(PAYMENT_STATUS)}
            />
            <FilterSelect
              testId={ADMIN_ORDERS.filterArtworkStatus}
              label="Artwork"
              value={artworkStatus}
              onChange={(v) => update({ artwork_status: v })}
              options={toOptions(ARTWORK_STATUS)}
            />
            <FilterSelect
              testId={ADMIN_ORDERS.filterProduct}
              label="Produk"
              value={product}
              onChange={(v) => update({ product_code: v })}
              options={[
                ["STANDARD", "Standard"],
                ["PREMIUM", "Premium"],
              ]}
            />
          </div>

          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid={ADMIN_ORDERS.clearFilters}
              className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/15 px-3.5 py-2 text-sm text-[#e8f0ec] hover:bg-white/5 transition"
            >
              <X strokeWidth={1.75} className="h-4 w-4" />
              Kosongkan Penapis
            </button>
          )}
        </div>

        {/* States */}
        {loading && (
          <div
            className="mt-10 grid place-items-center py-16"
            data-testid={ADMIN_ORDERS.loading}
          >
            <Loader2 className="h-6 w-6 animate-spin text-[#c79a3b]" strokeWidth={2} />
          </div>
        )}

        {!loading && error && (
          <div
            className="mt-8 rounded-2xl bg-[#3a1512] ring-1 ring-[#ff6b6b]/30 p-6 text-center"
            data-testid={ADMIN_ORDERS.error}
          >
            <p className="text-sm text-[#ffb4a2]">
              Gagal memuatkan tempahan. Sila cuba lagi.
            </p>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div
            className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-10 text-center"
            data-testid={ADMIN_ORDERS.empty}
          >
            <p className="text-sm text-[#a9c4b8]">
              {hasFilters
                ? "Tiada tempahan sepadan dengan carian/penapis anda."
                : "Belum ada tempahan lagi."}
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden md:block overflow-hidden rounded-2xl ring-1 ring-white/10">
              <table
                className="w-full text-sm"
                data-testid={ADMIN_ORDERS.table}
              >
                <thead className="bg-white/[0.04] text-[#a9c4b8]">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">No. Tempahan</th>
                    <th className="px-4 py-3 font-semibold">Tarikh</th>
                    <th className="px-4 py-3 font-semibold">Nama Perniagaan</th>
                    <th className="px-4 py-3 font-semibold">Design</th>
                    <th className="px-4 py-3 font-semibold">Produk</th>
                    <th className="px-4 py-3 font-semibold text-right">Kuantiti</th>
                    <th className="px-4 py-3 font-semibold text-right">Jumlah</th>
                    <th className="px-4 py-3 font-semibold">Bayaran</th>
                    <th className="px-4 py-3 font-semibold">Status Tempahan</th>
                    <th className="px-4 py-3 font-semibold">Artwork</th>
                    <th className="px-4 py-3 font-semibold text-right">Tindakan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {items.map((o) => (
                    <tr
                      key={o.order_reference}
                      data-testid={`${ADMIN_ORDERS.row}-${o.order_reference}`}
                      className="hover:bg-white/[0.03] transition"
                    >
                      <td className="px-4 py-3 font-mono text-[#e0b455]">
                        {o.order_reference}
                      </td>
                      <td className="px-4 py-3 text-[#c9d8d1] whitespace-nowrap">
                        {formatDate(o.created_at)}
                      </td>
                      <td className="px-4 py-3 text-white">{o.nama_perniagaan}</td>
                      <td className="px-4 py-3 text-[#c9d8d1]">{o.template_name}</td>
                      <td className="px-4 py-3 text-[#c9d8d1]">{o.product_name}</td>
                      <td className="px-4 py-3 text-right text-[#c9d8d1]">
                        {o.quantity}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-white">
                        {money(o.grand_total)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge map={PAYMENT_STATUS} value={o.payment_status} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge map={ORDER_STATUS} value={o.order_status} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge map={ARTWORK_STATUS} value={o.artwork_status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/orders/${o.order_reference}`}
                          data-testid={`${ADMIN_ORDERS.view}-${o.order_reference}`}
                          className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-white/15 px-3 py-1.5 text-xs text-[#e0b455] hover:bg-white/5 transition"
                        >
                          <Eye strokeWidth={1.75} className="h-3.5 w-3.5" />
                          Lihat
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 md:hidden space-y-3">
              {items.map((o) => (
                <div
                  key={o.order_reference}
                  data-testid={`${ADMIN_ORDERS.row}-${o.order_reference}`}
                  className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-sm text-[#e0b455]">
                        {o.order_reference}
                      </p>
                      <p className="mt-0.5 text-xs text-[#a9c4b8]">
                        {formatDate(o.created_at)}
                      </p>
                    </div>
                    <p className="font-semibold text-white">{money(o.grand_total)}</p>
                  </div>
                  <p className="mt-3 font-semibold text-white">{o.nama_perniagaan}</p>
                  <p className="text-xs text-[#a9c4b8]">
                    {o.template_name} · {o.product_name} · {o.quantity} keping
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge map={PAYMENT_STATUS} value={o.payment_status} />
                    <Badge map={ORDER_STATUS} value={o.order_status} />
                    <Badge map={ARTWORK_STATUS} value={o.artwork_status} />
                  </div>
                  <Link
                    to={`/admin/orders/${o.order_reference}`}
                    data-testid={`${ADMIN_ORDERS.view}-${o.order_reference}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg ring-1 ring-white/15 px-3 py-2 text-xs text-[#e0b455] hover:bg-white/5 transition"
                  >
                    <Eye strokeWidth={1.75} className="h-3.5 w-3.5" />
                    Lihat Butiran
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => update({ page: String(page - 1) }, false)}
                disabled={page <= 1}
                data-testid={ADMIN_ORDERS.prev}
                className="inline-flex items-center gap-1.5 rounded-xl ring-1 ring-white/15 px-3.5 py-2 text-sm text-[#e8f0ec] hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft strokeWidth={1.75} className="h-4 w-4" />
                Sebelum
              </button>
              <p className="text-sm text-[#a9c4b8]" data-testid={ADMIN_ORDERS.pageInfo}>
                Halaman {data.page} daripada {totalPages}
              </p>
              <button
                type="button"
                onClick={() => update({ page: String(page + 1) }, false)}
                disabled={page >= totalPages}
                data-testid={ADMIN_ORDERS.next}
                className="inline-flex items-center gap-1.5 rounded-xl ring-1 ring-white/15 px-3.5 py-2 text-sm text-[#e8f0ec] hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Seterusnya
                <ChevronRight strokeWidth={1.75} className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
